/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';

const ProjectRoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    role: '',
    description: ''
  });

  // Filter roles by search term
  const filteredRoles = roles.filter(role => {
    return searchTerm === '' ||
      role.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Sorting hook
  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredRoles,
    { defaultSortColumn: 'role', defaultSortOrder: 'asc' }
  );

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/api/project-roles');
      setRoles(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching project roles:', error);
      setError('Failed to load project roles');
      setLoading(false);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        role: role.role,
        description: role.description
      });
    } else {
      setEditingRole(null);
      setFormData({
        role: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setFormData({
      role: '',
      description: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.role || !formData.description) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (editingRole) {
        await axios.put(`/api/project-roles/${editingRole._id}`, formData);
        toast.success('Project role updated successfully');
        handleCloseDialog();
      } else {
        await axios.post('/api/project-roles', formData);
        toast.success('Project role created successfully');
        setFormData({ role: '', description: '' });
      }

      fetchRoles();
    } catch (error) {
      console.error('Error saving project role:', error);
      toast.error(error.response?.data?.error || 'Failed to save project role');
    }
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) {
      return;
    }

    try {
      await axios.delete(`/api/project-roles/${roleId}`);
      toast.success('Project role deleted successfully');
      fetchRoles();
    } catch (error) {
      console.error('Error deleting project role:', error);
      toast.error(error.response?.data?.error || 'Failed to delete project role');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Project Roles</Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          label="Search Roles"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by role name, description..."
          sx={{ minWidth: 300 }}
        />
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenDialog()}
        >
          Add New Role
        </Button>
        <Button variant="outlined" onClick={resetSort}>Reset Sort</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <SortableTableHeader column="role" label="Role" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
              </TableCell>
              <TableCell>
                <SortableTableHeader column="description" label="Description" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((role) => (
              <TableRow key={role._id}>
                <TableCell>{role.role}</TableCell>
                <TableCell>{role.description}</TableCell>
                <TableCell>
                  <IconButton aria-label="Edit"
                    size="small"
                    onClick={() => handleOpenDialog(role)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton aria-label="Delete"
                    size="small"
                    color="error"
                    onClick={() => handleDelete(role._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Project Role' : 'Add New Project Role'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            required
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ProjectRoleManagement;